/**
 * A tiny bridge that lets the plain fetch clients (`api/client.ts`, `api/portal.ts`) reach the
 * AppProvider's coordinated token refresh and its session-expired handling without importing React
 * state. AppProvider registers the implementation on mount; before that (or in tests) the calls are
 * safe no-ops.
 *
 * The clients use this to implement "on 401, refresh once and retry; if the refresh fails, send the
 * user to sign-in preserving where they were".
 */
interface AuthBridge {
  /**
   * Force a coordinated (cross-tab) refresh, bypassing the in-memory freshness check — the server
   * rejected a token we thought was still valid. Resolves to the new access token, or `null` when the
   * refresh token has expired (i.e. the user is signed out).
   */
  forceRefresh: () => Promise<string | null>;
  /** Terminal auth failure: drop the session and redirect to sign-in, keeping the current location. */
  onSessionExpired: () => void;
}

let bridge: AuthBridge | null = null;

export function registerAuthBridge(next: AuthBridge): () => void {
  bridge = next;
  return () => {
    if (bridge === next) bridge = null;
  };
}

/** Force a coordinated token refresh; `null` when unavailable or signed out. */
export function forceRefreshToken(): Promise<string | null> {
  return bridge ? bridge.forceRefresh() : Promise.resolve(null);
}

/** Signal a terminal auth failure so AppProvider can redirect to sign-in with a `returnTo`. */
export function handleSessionExpired(): void {
  bridge?.onSessionExpired();
}
